// giftCards (alias of customers; platform target-model §9.3, decided L8): the stored-value liability ledger on stream
// `gift_card:<id>`. Event payloads decode to exactly core's `GiftCardEvent` shapes (minus `type`). Cards are
// account-wide and their balance is the cloud's (L19): looking a card up and redeeming it need the internet, while
// loads ride on the order (a `gift_card_load` line) and post to the ledger when the order closes. The Admin Center
// settlement summary says who owes whom for the cards sold and spent across locations.
import { z } from 'zod';
import { defineCommand, defineQuery } from '../../define/operations.ts';
import { defineError } from '../../define/errors.ts';
import { defineEvent } from '../../define/event.ts';
import { secret } from '../../define/secret.ts';
import { EmployeeId, GiftCardId, IsoInstant, LocalDate, LocationId, Money, OrderId, OrderLineId, TenderId } from '../../primitives/codecs.ts';

/** What a person types or a reader swipes: 12–20 digits (legacy `/v1/giftcards/balance/{number}`). */
export const GiftCardNumber = z.string().regex(/^\d{12,20}$/).meta({ id: 'GiftCardNumber' });
const Last4 = z.string().regex(/^\d{4}$/);

export const GiftCardBalance = z
  .object({ giftCardId: GiftCardId, cardLast4: Last4, balance: Money })
  .meta({ id: 'GiftCardBalance' });

/** Legacy answered 200 `{found:false}` for an unknown number; the register then sells it as a new card. */
export const GiftCardLookup = z
  .discriminatedUnion('found', [
    z.object({ found: z.literal(false) }),
    z.object({ found: z.literal(true), giftCardId: GiftCardId, cardLast4: Last4, balance: Money }),
  ])
  .meta({ id: 'GiftCardLookup' });

// ---- errors ----------------------------------------------------------------------------------------------------------
export const GiftCardNotFound = defineError({
  code: 'giftCards.not_found', httpStatus: 404, message: 'errors.giftCards.not_found', retryable: false,
  details: z.object({ giftCardId: GiftCardId }),
});
/** CD-T05: the card holds less than the tender asks for (the balance it holds is in the details). */
export const GiftCardInsufficientBalance = defineError({
  code: 'giftCards.insufficient_balance', httpStatus: 409, message: 'errors.giftCards.insufficient_balance', retryable: false,
  details: z.object({ balance: Money }),
});
/** Any other rule core's gift-card ledger refuses (`rule` is core's error code, e.g. `redemption_conflict`). */
export const GiftCardRuleViolation = defineError({
  code: 'giftCards.rule_violation', httpStatus: 409, message: 'errors.giftCards.rule_violation', retryable: false,
  details: z.object({ rule: z.string() }),
});
// ---- events ----------------------------------------------------------------------------------------------------------
// Cloud facts: devices never pull the ledger (they ask the cloud for a balance).
const card = { aggregate: 'gift_card', deviceConsumed: false } as const;

export const GiftCardIssued = defineEvent({
  ...card,
  name: 'GiftCardIssued',
  version: 1,
  summary: 'A gift card number was sold for the first time; the ledger keeps only its last four digits.',
  payload: z.object({ giftCardId: GiftCardId, currency: z.enum(['USD', 'CAD']), cardLast4: Last4 }),
  verifiable: ['giftCardId', 'currency'],
  merge: 'lifecycle',
});

export const GiftCardLoaded = defineEvent({
  ...card,
  name: 'GiftCardLoaded',
  version: 1,
  summary: 'Money was loaded onto a card by a closed order\'s gift-card line (once per line).',
  payload: z.object({
    loadId: z.string().min(1).max(80), amount: Money, orderId: OrderId.optional(), lineId: OrderLineId.optional(), employeeId: EmployeeId.optional(),
  }),
  verifiable: ['loadId', 'amount'],
  merge: 'lifecycle',
});

export const GiftCardRedeemed = defineEvent({
  ...card,
  name: 'GiftCardRedeemed',
  version: 1,
  summary: 'A gift-card tender spent money from the card (its tip included).',
  payload: z.object({ tenderId: TenderId, orderId: OrderId, amount: Money, tip: Money, employeeId: EmployeeId.optional() }),
  verifiable: ['tenderId', 'amount', 'tip'],
  merge: 'lifecycle',
});

export const GiftCardTipRedeemed = defineEvent({
  ...card,
  name: 'GiftCardTipRedeemed',
  version: 1,
  summary: 'A gift-card tender\'s tip was set after the payment: the card moved by the change (signed); the ledger counted `counted` (the whole tip under legacy\'s counting, B224).',
  payload: z.object({ tenderId: TenderId, orderId: OrderId, tip: Money, change: Money, counted: Money, employeeId: EmployeeId.optional() }),
  verifiable: ['tenderId', 'tip', 'change', 'counted'],
  merge: 'lifecycle',
});

export const GiftCardRedemptionReversed = defineEvent({
  ...card,
  name: 'GiftCardRedemptionReversed',
  version: 1,
  summary: 'A voided or refunded gift-card tender gave its money back to the card.',
  payload: z.object({ tenderId: TenderId, amount: Money, tip: Money, reason: z.enum(['void', 'refund']) }),
  verifiable: ['tenderId', 'amount', 'tip'],
  merge: 'lifecycle',
});

export const GiftCardLoadVoided = defineEvent({
  ...card,
  name: 'GiftCardLoadVoided',
  version: 1,
  summary: 'The order that loaded money onto a card was voided; the load is taken back off the card.',
  payload: z.object({ loadId: z.string().min(1).max(80), amount: Money }),
  verifiable: ['loadId', 'amount'],
  merge: 'lifecycle',
});

export const GiftCardAdjusted = defineEvent({
  ...card,
  name: 'GiftCardAdjusted',
  version: 1,
  summary: 'A card\'s balance was cleared to zero or corrected by hand (signed amount).',
  payload: z.object({
    adjustmentId: z.string().min(1).max(64), amount: Money, reason: z.enum(['cleared', 'correction']), note: z.string().max(500).optional(),
    employeeId: EmployeeId.optional(),
  }),
  verifiable: ['adjustmentId', 'amount'],
  merge: 'lifecycle',
});

/** Every event the gift-card ledger records, in the order core's `GiftCardEvent` union lists them. */
export const giftCardEvents = [
  GiftCardIssued, GiftCardLoaded, GiftCardRedeemed, GiftCardTipRedeemed, GiftCardRedemptionReversed, GiftCardLoadVoided, GiftCardAdjusted,
] as const;

const SettlementDirection = z.enum(['location_owes_corporate', 'corporate_owes_location', 'settled']);

// ---- commands --------------------------------------------------------------------------------------------------------
const online = {
  availability: 'cloud',
  cloudReason: 'Gift cards are account-wide stored value; only the cloud holds every location\'s redemptions, so a balance read or spent elsewhere could be overdrawn (L19).',
} as const;

export const RedeemGiftCard = defineCommand({
  ...online,
  origin: 'cloud',
  name: 'giftCards.redeem',
  summary: 'Spend from a gift card for a tender (the cloud approves it like a processor, before the tender is recorded).',
  input: z.object({
    giftCardId: GiftCardId, locationId: LocationId, tenderId: TenderId, orderId: OrderId, amount: Money, tip: Money, employeeId: EmployeeId.optional(),
  }),
  output: GiftCardBalance,
  permission: { session: 'device' },
  risk: 'low',
  dryRun: false,
  agent: { expose: false },
  emits: [GiftCardRedeemed],
  errors: [GiftCardNotFound, GiftCardInsufficientBalance, GiftCardRuleViolation],
  legacy: ['POST /v1/orders/{order_id}/tender (type gift)'],
});

/**
 * The tip on a gift-card tender, added after the payment (the Checkout tip view, the customer display): the card pays
 * the change to the tip it already carries, never more than it holds. The register records the tender's tip only after
 * this approves.
 */
export const RedeemGiftCardTip = defineCommand({
  ...online,
  origin: 'cloud',
  name: 'giftCards.redeemTip',
  summary: 'Set the tip on a gift-card tender the card already paid, taking the change from the card.',
  input: z.object({ giftCardId: GiftCardId, locationId: LocationId, tenderId: TenderId, orderId: OrderId, tip: Money, employeeId: EmployeeId.optional() }),
  output: GiftCardBalance,
  permission: { session: 'device' },
  risk: 'low',
  dryRun: false,
  agent: { expose: false },
  emits: [GiftCardTipRedeemed],
  errors: [GiftCardNotFound, GiftCardInsufficientBalance, GiftCardRuleViolation],
  legacy: ['PUT /v1/tenders/{id}/tip (type gift)'],
});

/**
 * The legacy import (lane M gap 9): a legacy card under its number, issued with legacy's balance (loads less gift tenders
 * with their tips) as an opening correction. A re-run moves only the difference to legacy's balance.
 */
export const ImportLegacyGiftCard = defineCommand({
  ...online,
  origin: 'cloud',
  name: 'giftCards.importLegacyCard',
  cloudReason: 'Only the legacy import writes it, in the cloud, before the store moves; cards are account-wide stored value the cloud holds (L19).',
  summary: "Bring a legacy gift card over with legacy's balance, so its number is found and spends what legacy said it held.",
  // A card number is a bearer value (D7): only its digest and last four are kept.
  // Legacy's stored number as is: its API took any string, so some cards sit outside the 12-20 digits a person can enter.
  input: z.object({ giftCardId: GiftCardId, locationId: LocationId, cardNumber: secret(z.string().min(1).max(255)), balance: Money }),
  output: GiftCardBalance,
  permission: 'customers.manage',
  risk: 'high',
  dryRun: true,
  agent: { expose: false },
  emits: [GiftCardIssued, GiftCardAdjusted],
  errors: [GiftCardRuleViolation],
  legacy: ['gift_cards', 'gift_card_transactions (type loading)', 'tenders (type gift)'],
});

// ---- queries ---------------------------------------------------------------------------------------------------------
export const LookupGiftCard = defineQuery({
  ...online,
  name: 'giftCards.lookup',
  summary: 'A gift card\'s balance by its number (double swipes cleaned up), or not found.',
  // A card number is a bearer value (D7): never logged or stored by the lookup.
  input: z.object({ cardNumber: secret(GiftCardNumber) }),
  output: GiftCardLookup,
  permission: { session: 'device' },
  scope: 'account',
  errors: [],
  agent: { expose: false },
  legacy: ['GET /v1/giftcards/balance/{number}', 'GET /v1/giftcards/{id}'],
});

const SettlementFigures = z.object({ loaded: Money, redeemed: Money, reserve: Money, net: Money, direction: SettlementDirection, amount: Money });

export const GiftCardSettlementSummary = defineQuery({
  ...online,
  name: 'giftCards.settlementSummary',
  summary: 'Unsettled gift-card loads and redemptions of the account and of each location, with the reserve and who owes whom.',
  input: z.object({}),
  output: z.object({
    account: SettlementFigures,
    locations: z.array(SettlementFigures.extend({ locationId: LocationId, locationName: z.string(), bankDetailsMissing: z.boolean() })),
  }),
  permission: 'giftCards.settle',
  scope: 'account',
  errors: [],
  agent: { expose: false },
  legacy: ['GET /v1/admin-center/gift-cards/account-summary', 'GET /v1/admin-center/gift-cards/location-summary'],
});

const ReportRow = z.object({ at: IsoInstant, cardMasked: z.string(), employeeName: z.string().nullable(), amount: Money });

export const GiftCardsReport = defineQuery({
  ...online,
  name: 'giftCards.report',
  // E-09: a back-office session also needs Sales Reports (the report sits under Sales Reports).
  backofficeAlso: ['reports.sales.view'],
  summary: 'Gift Cards report: cards loaded and redeemed at a location over business dates, with totals.',
  input: z.object({ locationId: LocationId, from: LocalDate, to: LocalDate }),
  output: z.object({
    loadedCount: z.int().nonnegative(), loadedAmount: Money, redeemedCount: z.int().nonnegative(), redeemedAmount: Money,
    loads: z.array(ReportRow),
    redemptions: z.array(ReportRow.extend({ ticket: z.string().nullable() })),
  }),
  permission: 'reports.view',
  scope: 'location',
  errors: [],
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  legacy: ['GET /v1/reports/gift-cards'],
});

/**
 * The Gift Cards report's "Download Balance Sheet" (legacy GiftCardBalanceSheetController), Radish staff only as in legacy
 * (`loggedInAsAdmin || loggedInAsReseller`, Q-RPT-14): every card of the account with what was loaded onto it (loads,
 * clears and corrections; an imported card's legacy balance is its opening load), what was spent from it (tenders with
 * their tips, net of voids and refunds; legacy left the tips out, bug B276, fixed by L418), its balance, and the location of
 * its first load. Card numbers stay masked (L176: the ledger keeps no number).
 */
export const GiftCardBalanceSheet = defineQuery({
  ...online,
  name: 'giftCards.balanceSheet',
  summary: "Every gift card of the account with its loaded and spent totals, its balance and the location of its first load (Radish staff only).",
  input: z.object({}),
  output: z.object({
    cards: z.array(z.object({ cardMasked: z.string(), loaded: Money, spent: Money, balance: Money, locationName: z.string() })),
  }),
  permission: 'settings.platform.manage',
  scope: 'account',
  errors: [],
  agent: { expose: false },
  legacy: ['GET /v1/giftcards/balance-sheet'],
});

export const giftCardCommands = [RedeemGiftCard, RedeemGiftCardTip, ImportLegacyGiftCard] as const;
export const giftCardQueries = [LookupGiftCard, GiftCardSettlementSummary, GiftCardsReport, GiftCardBalanceSheet] as const;
